import React, { memo, useState, useCallback } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import CustomButton from 'components/CustomButton';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import { useNavigateWithClient } from 'hooks';
import { CustomTooltip } from 'components/InfoTooltip';
import { useServices, useSnackbar } from 'contexts';
import { getCaseMasterProblems } from 'services';
import { Session } from 'utils';
import NoAccessTooltip, { NO_BATCH_ACCESS_MESSAGE } from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';
import { useCaseMasterAccess } from './CaseMasterAccessContext';
import CaseMasterLockMessage from './CaseMasterLockMessage';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    boxShadow: "0px 0px 4px 0px #00000026",
    borderRadius: '4px',
    padding: theme.spacing(7, 10),
  },
  header: {
    borderBottom: '1px solid #8692A3',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '22px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  content: {
    marginTop: theme.spacing(7),
  },
  cards: {
    display: 'flex', flexDirection: 'column', gap: '20px',
    flexWrap: 'wrap', marginTop: '12px'
  },
  cardRoot: {
    border: '2px solid',
    width: '100%',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(7, 6),
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary['clr-100'],
    }
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  skeletonCardRoot: {
    width: '100%',
    border: '2px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(7, 6),
  },
  skeleton: {
    borderRadius: '4px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  viewFeedbackLink: {
    color: '#1961BF',
    cursor: 'pointer',
  },
}));

const Card = memo(({
  _id, attempts = [], title = '', entryPoint = null, feedbackAttemptId = null
}) => {
  const classes = useStyles();
  const navigateWithClient = useNavigateWithClient();
  const removed = useSelectedBatchRemoved();

  const [startInterview, setStartInterview] = useState(false);

  const hasAttempted = attempts?.length > 0;

  // Plan-based gating via the shared access context: non-B2C → unlocked; B2C
  // free → only the flagged free case until its attempt is spent; Pro → until
  // validity; Premium → always. Batch removal takes priority over the message.
  const { getLockInfo } = useCaseMasterAccess();
  const { locked, message: lockMessage, upgradeUrl, linkText } = getLockInfo(_id);
  const disabled = removed || locked;
  const tooltipMessage = removed
    ? NO_BATCH_ACCESS_MESSAGE
    : <CaseMasterLockMessage
      message={lockMessage} upgradeUrl={upgradeUrl} linkText={linkText}
    />;
  const lockIcon = locked ? <LockOutlinedIcon sx={{ fontSize: '18px' }} /> : undefined;

  const handleOpenDialog = useCallback(() => setStartInterview(true), []);
  const handleCloseDialog = useCallback(() => setStartInterview(false), []);
  const handleViewFeedback = useCallback(() => {
    navigateWithClient(`/performance/case-master/${feedbackAttemptId}`);
  }, [navigateWithClient, feedbackAttemptId]);

  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.cardContent}>
        <Typography variant='h4-medium'>
          {title}
        </Typography>
        <Box display='flex' gap={1} mt={1} alignItems='center'>
          {hasAttempted ? (
            <>
              <Typography variant='body01-bold' color='neutral.clr-600'>
                Attempted
              </Typography>
              <CheckCircleIcon sx={{ fontSize: '20px', color: '#22C55E' }} />
            </>
          ) : (
            <Typography variant='body01-bold' color='neutral.clr-600'>
              Unattempted
            </Typography>
          )}
        </Box>
      </Box>
      {
        !hasAttempted ?
          <NoAccessTooltip show={disabled} message={tooltipMessage}>
            <CustomButton
              onClick={handleOpenDialog} variant='contained' color='primary'
              disabled={disabled} startIcon={lockIcon}
            >
              Attempt now
            </CustomButton>
          </NoAccessTooltip>
          :
          <Box className={classes.buttonGroup}>
            <NoAccessTooltip show={disabled} message={tooltipMessage}>
              <CustomButton
                onClick={handleOpenDialog} variant='outlined' color='primary'
                disabled={disabled} startIcon={lockIcon}
              >
                Reattempt
              </CustomButton>
            </NoAccessTooltip>
            {
              feedbackAttemptId &&
              <Typography
                onClick={handleViewFeedback}
                variant='body01-link'
                className={classes.viewFeedbackLink}
              >
                View feedback
              </Typography>
            }
          </Box>
      }
      <StartAssessmentDialog
        onClose={handleCloseDialog}
        open={startInterview}
        template={{ _id: _id, name: title, type: 'case_master' }}
        codingInterview={false}
        stateParams={entryPoint}
      />
    </Box>
  );
});

const CardSkeleton = () => {
  const classes = useStyles();

  return (
    <Box className={classes.skeletonCardRoot}>
      <Box className={classes.cardContent}>
        <Skeleton variant="text" width={200} height={32} className={classes.skeleton} />
        <Box display='flex' gap={2} mt={1} alignItems='center'>
          <Skeleton variant="text" width={100} height={24} className={classes.skeleton} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" width={120} height={40} className={classes.skeleton} />
    </Box>
  );
};

const Challenges = ({
  subTopic = null, handleBack = () => { }, entryPoint = null
}) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [challenges, setChallenges] = useState([]);
  const [completionRate, setCompletionRate] = useState(0);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const filters = {};

        // Send topic name instead of tags
        if (subTopic?.topicKey) {
          filters.topic = subTopic.topicKey;
        } else if (subTopic?.name) {
          filters.topic = subTopic.name.toLowerCase().replace(/\s+/g, '_');
        }

        const result = await service.callService(
          getCaseMasterProblems, filters
        );

        // Handle new response structure with casemasterProblems array
        const problems = result?.casemasterProblems || [];
        
        let totalChallenges = problems.length;
        let totalCompleted = 0;

        // Determine attempts based on attemptedBy list for the current user
        const currentUserId = Session.userId?.toString();
        problems.forEach((problem, i) => {
          const attemptedBy = problem.attemptedBy || [];
          const hasAttempted = currentUserId ? attemptedBy.includes(currentUserId) : false;

          // Normalize to the existing Card API: attempts length indicates whether user has attempted
          problems[i].attempts = hasAttempted ? [{}] : [];

          if (hasAttempted) totalCompleted += 1;
        });

        setChallenges(problems);
        setCompletionRate(totalChallenges > 0 ? (totalCompleted / totalChallenges) * 100 : 0);

      } catch (error) {
        console.error('Error fetching case studies:', error);
        snackbar.error(error.message || "Uh Oh! Unable to get case studies. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [subTopic?.topicKey, subTopic?.name]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <ArrowBackIcon style={{ cursor: 'pointer', color: '#0062E1' }} onClick={handleBack} />
          <Typography variant='h5-medium'>
            {subTopic?.name?.charAt(0).toUpperCase() + subTopic?.name?.slice(1) || 'Case Studies'}
          </Typography>
        </Box>
        <CustomTooltip
          component='It represents your progress for this topic. It is based on your attempted case studies'
        >
          <Typography
            variant='h6-medium'
            color='primary.clr-300'
            style={{ borderBottom: '2px dotted #135EBF' }}
          >
            Completed: {completionRate.toFixed(0)}%
          </Typography>
        </CustomTooltip>
      </Box>

      <Box className={classes.content}>
        {loading ? (
          <Box className={classes.cards}>
            {[...Array(3)].map((_, index) => (
              <CardSkeleton key={`card-skeleton-${index}`} />
            ))}
          </Box>
        ) : (
          <>
            {challenges.length > 0 ? (
              <Box className={classes.cards}>
                {challenges.map((data, i) => (
                  <Card
                    key={data._id || i}
                    {...data}
                    attempts={data.attempts || []}
                    entryPoint={entryPoint}
                  />
                ))}
              </Box>
            ) : (
              <Box mt={4} textAlign="center">
                <Typography variant='body01' color='neutral.clr-600'>
                  No case studies available for this topic yet.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default Challenges;
